import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { FaBuilding, FaUserTie, FaUserCircle, FaHandshake, FaCheck } from "react-icons/fa";
import { useAuth } from "../contexts/AuthContext";
import { useRoleTabs } from "../hooks/useRoleTabs";
import { useActiveCargo } from "../lib/activeCargo";
import { normalizeRoles, permsForRole } from "../lib/roles";

// Seletor de perfil para contas multi-papel. Renderiza a seção "Trocar perfil"
// (uma entrada por ÁREA navegável) dentro de um dropdown. Retorna null quando a
// conta tem só uma área — assim pode ser colocado em qualquer menu sem checagem.
// `onNavigate` é chamado ao clicar (use para fechar o dropdown que o contém).
const COMPANY_LEGACY = {
  manager: { label: "Gerente", route: "/company/manager/dashboard" },
  formatter: { label: "Formatador", route: "/company/formatter/dashboard" },
  linguist: { label: "Linguista", route: "/company/linguist/dashboard" },
  reviewer: { label: "Revisor", route: "/company/reviewer/dashboard" },
  translator: { label: "Tradutor", route: "/company/translator/dashboard" },
};

const ProfileSwitcher = ({ onNavigate }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeCargo } = useActiveCargo();
  const swCargo = activeCargo || user?.employeeRole;
  const { tabs: employeeTabs } = useRoleTabs(swCargo, permsForRole(user, swCargo));

  const roles = normalizeRoles(user?.userType);
  const areas = [];
  if (roles.includes("master"))
    areas.push({ key: "master", label: t("roles.master", "Master"), route: "/company/master/dashboard", icon: FaBuilding, prefix: "/company/master" });
  if (roles.includes("employee"))
    areas.push({ key: "employee", label: t("roles.employee", "Colaborador"), route: employeeTabs[0]?.route || "/employee/files", icon: FaUserTie, prefix: "/employee" });
  Object.entries(COMPANY_LEGACY).forEach(([r, info]) => {
    if (roles.includes(r))
      areas.push({ key: r, label: info.label, route: info.route, icon: FaUserTie, prefix: info.route });
  });
  if (roles.some((r) => ["b2b", "b2c", "colab"].includes(r)))
    areas.push({ key: "client", label: t("roles.client", "Cliente"), route: "/client/projects", icon: FaUserCircle, prefix: "/client" });
  if (roles.includes("affiliates"))
    areas.push({ key: "affiliates", label: t("roles.affiliate", "Afiliado"), route: "/affiliates/manage", icon: FaHandshake, prefix: "/affiliates" });

  if (areas.length <= 1) return null;

  const currentArea = areas.find((a) => location.pathname.startsWith(a.prefix))?.key || null;

  const go = (area) => {
    if (onNavigate) onNavigate();
    if (area.key !== currentArea) navigate(area.route);
  };

  return (
    <div className="border-b border-slate-100 mb-1 pb-1">
      <p className="px-3 pt-1 pb-1 text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
        {t("common.switchProfile", "Trocar perfil")}
      </p>
      {areas.map((area) => {
        const Icon = area.icon;
        const active = area.key === currentArea;
        return (
          <button
            key={area.key}
            onClick={() => go(area)}
            className={`flex items-center w-full px-3 py-2 text-sm transition-colors duration-200 ${
              active ? "bg-blue-50 text-blue-700 font-medium" : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            <Icon className={`w-4 h-4 mr-2.5 ${active ? "text-blue-600" : "text-slate-400"}`} />
            <span className="flex-1 text-left">{area.label}</span>
            {active && <FaCheck className="w-3 h-3 text-blue-600" />}
          </button>
        );
      })}
    </div>
  );
};

export default ProfileSwitcher;
